"use client";
import useTranslate from "@app/_hooks/useTranslate";
import { ArrowRight as ArrowIcon } from "lucide-react";

import { useMarket } from "@features/market/useMarket";
import Link from "@components/Link";
import { MarketSelectorButton } from "@components/MarketSelector";
import { Divider } from "@components/ngg";
import { useHeaderContext } from "@context/headerContext";

import { FEATURE_FLAGS } from "@/config/featureFlags";
import { useMenuStore } from "@/stores/menuStore";
import { getOpeningHoursText } from "@/utils/common";
import AuthButton from "@features/auth/components/AuthButton";

const ContactLinks = () => {
  const { locale, contactNavigation: links } = useHeaderContext();
  const setMobileMenuOpen = useMenuStore((state) => state.setMobileMenuOpen);

  if (!links.length) {
    return null;
  }

  return (
    <div className="space-y-3 pt-4">
      {links.map((link, index) => (
        <p key={`${link?.slug}-${index}`} className="text-base font-normal">
          <Link
            locale={locale}
            href={link?.slug}
            onClick={() => setMobileMenuOpen(false)}
          >
            {link?.title}
          </Link>
        </p>
      ))}
    </div>
  );
};

const CustomerServiceInfo = () => {
  const t = useTranslate();
  const setMobileMenuOpen = useMenuStore((state) => state.setMobileMenuOpen);

  const { services, locale } = useHeaderContext();
  const { contentfulLocale } = useMarket();
  const customerService = services?.at(0);
  if (!customerService) return null;

  const openingHours = getOpeningHoursText({
    contentfulLocale,
    store: customerService,
    t,
  });

  return (
    <div className="pt-4">
      <p className="mb-2 text-sm font-normal uppercase">{t("contactUs")}</p>
      {FEATURE_FLAGS.customerService.phoneLink && (
        <p>
          {t("callUsAt")}
          <a
            className="ml-1 underline"
            href={`tel:${customerService.phoneNumber?.replace(/[-\s]/g, "")}`}
          >
            {customerService.phoneNumber}
          </a>
        </p>
      )}
      {FEATURE_FLAGS.customerService.emailLink && (
        <p>
          {t("emailUsAt")}
          <a
            href={`mailto:${customerService.email}`}
            className="ml-1 underline"
          >
            {customerService?.email}
          </a>
        </p>
      )}

      <p>{openingHours}</p>
      <p>
        <Link
          locale={locale}
          className="underline"
          href={customerService.storePage?.slug}
          onClick={() => setMobileMenuOpen(false)}
        >
          {customerService.storePageLinkText}
        </Link>
        <ArrowIcon strokeWidth={1} className="mb-1 inline size-5 -rotate-45" />
      </p>
    </div>
  );
};

const StoreInfo = () => {
  const t = useTranslate();
  const { locale, stores } = useHeaderContext();
  const { contentfulLocale } = useMarket();
  const setMobileMenuOpen = useMenuStore((state) => state.setMobileMenuOpen);

  if (!stores?.length) return null;

  return (
    <div className="pt-4">
      <p className="mb-2 text-sm font-normal uppercase">{t("ourStores")}</p>
      <div className="space-y-4">
        {stores.map((store) => {
          const openingHours = getOpeningHoursText({
            contentfulLocale,
            store,
            t,
          });
          return (
            <div key={store?.sys?.id}>
              <p>{`${store?.title}, ${store?.address}`}</p>
              <p>{openingHours}</p>
              <Link
                locale={locale}
                className="underline"
                href={store.storePage?.slug}
                onClick={() => setMobileMenuOpen(false)}
              >
                {store?.storePageLinkText}
                <ArrowIcon
                  strokeWidth={1}
                  className="mb-1 inline size-5 -rotate-45"
                />
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function MobileMenuInfo() {
  return (
    <div className="bg-gray-100 px-4 pb-6 lg:hidden">
      <MarketSelectorButton className="w-full content-start gap-1 py-4" />
      <Divider className="-mx-4 h-px" />
      <AuthButton />
      <Divider className="-mx-4 h-px" />
      <ContactLinks />
      <Divider className="-mx-4 mt-4 h-px" />
      <CustomerServiceInfo />
      <StoreInfo />
    </div>
  );
}
